import AsyncStorage from '@react-native-async-storage/async-storage';
import * as AppleAuthentication from 'expo-apple-authentication';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';

import { revenueCatService } from '../../features/monetization/services/revenueCatService';
import { useLanguageStore } from '../../features/quiz-core/languageStore';
import { t } from '../../i18n/translations';
import amplitude from '../../lib/amplitude';
import { track } from '../../lib/analytics';

import { supabase } from './supabaseService';

export interface User {
  id: string;
  email: string;
  name: string;
  language: string;
  created_at: string;
  updated_at: string;
  is_admin: boolean;
  onboarding_completed: boolean;
  referral_code: string;
  referred_by?: string;
  avatar_url?: string;
  church_id?: string;
  family_id?: string;
  total_referrals: number;
  successful_referrals: number;
}

export interface SignUpData {
  email: string;
  password: string;
  name: string;
  language?: string;
  referral_code?: string;
}

export interface SignInData {
  email: string;
  password: string;
}

export interface UpdateProfileData {
  name?: string;
  language?: string;
  avatar_url?: string;
  onboarding_completed?: boolean;
}

export interface LinkedAuthIdentity {
  identityId: string;
  provider: string;
  email?: string;
  lastSignInAt?: string;
  createdAt?: string;
}

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_POLICY_FALLBACK =
  'Password must be at least 8 characters and include one uppercase letter and one number.';

class AuthService {
  private currentUser: User | null = null;
  // Track recent re-auth time to gate sensitive actions
  private lastReauthAt: number | null = null;

  /**
   * Initialize auth service and check existing session
   */
  async initialize(): Promise<User | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        const user = await this.loadUserProfile(session.user.id);
        this.currentUser = user;

        await this.syncRevenueCatIdentity(user.id);

        // Set analytics user ID
        amplitude.setUserId(user.id);

        return user;
      }

      return null;
    } catch (error) {
      console.error('Failed to initialize auth service:', error);
      return null;
    }
  }

  /**
   * Re-authenticate with password (for email/password users)
   */
  async reauthenticateWithPassword(password: string): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    const email = userData.user?.email;
    if (!email) throw new Error('No authenticated user');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message || 'Failed to re-authenticate');
    this.lastReauthAt = Date.now();
    return true;
  }

  /**
   * Re-authenticate with OAuth provider
   */
  async reauthenticateWithProvider(provider: 'google' | 'apple'): Promise<boolean> {
    if (provider === 'google') {
      await this.signInWithGoogle();
    } else if (provider === 'apple') {
      await this.signInWithApple();
    }
    this.lastReauthAt = Date.now();
    return true;
  }

  /**
   * Check if user has recently re-authenticated within the last 10 minutes
   */
  private hasRecentReauth(): boolean {
    return this.lastReauthAt !== null && Date.now() - this.lastReauthAt < 10 * 60 * 1000;
  }

  /**
   * Mark the current user as re-authenticated
   * Used for OAuth users who have active sessions but don't need to re-authenticate
   */
  markReauthenticated(): void {
    this.lastReauthAt = Date.now();
    console.log('✅ [Auth] User marked as re-authenticated');
  }

  private getCurrentLanguage(fallback?: string): string {
    return fallback || useLanguageStore.getState().language || 'en';
  }

  private getPasswordPolicyMessage(language?: string): string {
    return t('auth.passwordPolicyError', this.getCurrentLanguage(language)) || PASSWORD_POLICY_FALLBACK;
  }

  private isPasswordPolicyCompliant(password: string): boolean {
    return (
      password.length >= PASSWORD_MIN_LENGTH &&
      /[A-Z]/.test(password) &&
      /\d/.test(password)
    );
  }

  private assertPasswordPolicy(password: string, language?: string): void {
    if (!this.isPasswordPolicyCompliant(password)) {
      throw new Error(this.getPasswordPolicyMessage(language));
    }
  }

  /**
   * Sign up new user with improved email confirmation flow
   */
  async signUp(signUpData: SignUpData): Promise<{ needsConfirmation: boolean; message: string }> {
    try {
      // Validate input data
      if (!signUpData.email || !signUpData.password || !signUpData.name) {
        throw new Error('Email, password, and name are required');
      }

      this.assertPasswordPolicy(signUpData.password, signUpData.language);

      console.log('Starting signup process');

      // Sign up with Supabase Auth - user profile will be created after email confirmation
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: signUpData.email.toLowerCase().trim(),
        password: signUpData.password,
        options: {
          data: {
            name: signUpData.name.trim(),
            language: signUpData.language || 'en',
            referral_code: signUpData.referral_code
          }
        }
      });

      if (authError) {
        console.error('Supabase auth signup error:', authError);
        throw new Error(this.getReadableAuthError(authError.message, signUpData.language));
      }

      if (!authData.user) {
        throw new Error('User creation failed - no user returned');
      }

      console.log('Auth user created successfully:', authData.user.id);

      // Check if email confirmation is required
      if (!authData.user.email_confirmed_at) {
        console.log('Email confirmation required');
        
        // Track signup attempt (not completed yet)
        track('user_signup_initiated', {
          user_id: authData.user.id,
          email: signUpData.email,
          language: signUpData.language || 'en',
          has_referral: !!signUpData.referral_code
        });

        return {
          needsConfirmation: true,
          message: 'Please check your email and click the confirmation link to complete your account setup.'
        };
      }

      // If email is already confirmed (shouldn't happen in normal flow, but handle it)
      console.log('Email already confirmed, creating profile immediately');
      await this.handleEmailConfirmation(authData.user);
      
      return {
        needsConfirmation: false,
        message: 'Account created successfully!'
      };

    } catch (error: any) {
      console.error('Signup error:', error);
      
      // Provide user-friendly error messages
      if (error.message.includes('already registered')) {
        throw new Error('An account with this email already exists. Please sign in instead.');
      }
      
      if (error.message.includes('invalid email')) {
        throw new Error('Please enter a valid email address.');
      }

      if (error.message.includes('weak password')) {
        throw new Error(this.getPasswordPolicyMessage(signUpData.language));
      }

      throw new Error(error.message || 'Failed to create account. Please try again.');
    }
  }

  /**
   * Handle email confirmation and create user profile
   */
  async handleEmailConfirmation(authUser: any): Promise<User> {
    try {
      // Generate referral code
      const referralCode = this.generateReferralCode();
      
      const userProfile: Partial<User> = {
        id: authUser.id,
        email: authUser.email,
        name: authUser.user_metadata?.name || authUser.email?.split('@')[0] || 'User',
        language: authUser.user_metadata?.language || 'en',
        is_admin: false,
        onboarding_completed: false,
        referral_code: referralCode,
        referred_by: authUser.user_metadata?.referral_code,
        total_referrals: 0,
        successful_referrals: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Create user profile after email confirmation
      const { data, error: profileError } = await supabase
        .from('user_profiles')
        .upsert(userProfile, {
          onConflict: 'id',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (profileError) {
        console.error('Profile creation error after confirmation:', profileError);
        throw profileError;
      }

      console.log('✅ User profile created after email confirmation');

      // Track referral if provided
      if (authUser.user_metadata?.referral_code) {
        try {
          await this.trackReferral(authUser.user_metadata.referral_code, authUser.id);
        } catch (error) {
          console.warn('Referral tracking failed:', error);
        }
      }

      // Track successful signup completion
      track('user_signed_up', {
        user_id: authUser.id,
        email: authUser.email,
        language: authUser.user_metadata?.language || 'en',
        has_referral: !!authUser.user_metadata?.referral_code
      });

      // Set analytics user ID
      amplitude.setUserId(authUser.id);

      // Set current user
      this.currentUser = data as User;

      return data as User;

    } catch (error: any) {
      console.error('Email confirmation handling error:', error);
      throw new Error(error.message || 'Failed to complete account setup');
    }
  }

  /**
   * Convert Supabase auth errors to user-friendly messages
   */
  private getReadableAuthError(errorMessage: string, language?: string): string {
    if (errorMessage.includes('User already registered')) {
      return 'An account with this email already exists';
    }
    if (errorMessage.includes('Invalid email')) {
      return 'Please enter a valid email address';
    }
    if (errorMessage.includes('Password should be at least')) {
      return this.getPasswordPolicyMessage(language);
    }
    if (errorMessage.includes('signup is disabled')) {
      return 'Account creation is temporarily disabled';
    }
    if (errorMessage.includes('unexpected_failure')) {
      return 'Server error occurred. Please try again in a moment.';
    }

    // Return original message if we can't make it more readable
    return errorMessage;
  }

  /**
   * Sign in existing user
   */
  async signIn(signInData: SignInData): Promise<User> {
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: signInData.email.toLowerCase().trim(),
        password: signInData.password
      });

      if (authError) {
        console.error('Sign in error:', authError);
        throw new Error(this.getReadableAuthError(authError.message));
      }

      if (!authData.user) {
        throw new Error('Sign in failed');
      }

      // Load user profile
      const user = await this.loadUserProfile(authData.user.id);
      this.currentUser = user;

      await this.syncRevenueCatIdentity(user.id);

      // Set analytics user ID
      amplitude.setUserId(user.id);

      // Track sign in event
      track('user_signed_in', {
        method: 'email',
        language: user.language
      });

      return user;
    } catch (error: any) {
      console.error('Sign in error:', error);
      throw new Error(error.message || 'Failed to sign in');
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      // Sign out with 'global' scope to clear OAuth sessions everywhere
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;

      await this.clearRevenueCatIdentity();

      // Clear current user
      this.currentUser = null;

      // Clear analytics user ID
      amplitude.setUserId(undefined);

      // Clear stored data
      await AsyncStorage.multiRemove([
        '@onboarding_progress',
        '@user_preferences',
        '@cached_questions'
      ]);

      // Track sign out event
      track('user_signed_out');
    } catch (error: any) {
      console.error('Sign out error:', error);
      throw new Error(error.message || 'Failed to sign out');
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(updates: UpdateProfileData): Promise<User> {
    try {
      if (!this.currentUser) throw new Error('No authenticated user');

      const updatedData = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('user_profiles')
        .update(updatedData)
        .eq('id', this.currentUser.id)
        .select()
        .single();

      if (error) throw error;

      // Update current user
      this.currentUser = { ...this.currentUser, ...data };

      // Track profile update
      track('user_profile_updated', {
        fields: Object.keys(updates),
        language: data.language
      });

      if (!this.currentUser) {
        throw new Error('Failed to update user profile');
      }
      return this.currentUser;
    } catch (error: any) {
      console.error('Profile update error:', error);
      throw new Error(error.message || 'Failed to update profile');
    }
  }

  /**
   * Load user profile from database
   */
  private async loadUserProfile(userId: string): Promise<User> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      if (!data) throw new Error('User profile not found');

      return data as User;
    } catch (error: any) {
      console.error('Load profile error:', error);
      throw new Error(error.message || 'Failed to load user profile');
    }
  }

  private async loadUserProfileOrNull(userId: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;

      return (data as User | null) ?? null;
    } catch (error: any) {
      console.error('Load optional profile error:', error);
      throw new Error(error.message || 'Failed to load optional user profile');
    }
  }

  /**
   * Generate unique referral code
   */
  private generateReferralCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Track referral usage
   */
  private async trackReferral(referralCode: string, newUserId: string): Promise<void> {
    track('referral_code_received', {
      referral_code: referralCode,
      new_user_id: newUserId,
      backend_status: 'disabled'
    });
  }

  /**
   * Complete referrals for a user (called when onboarding is finished)
   */
  async completeUserReferrals(userId: string): Promise<void> {
    void userId;
  }

  /**
   * Reset password
   */
  async resetPassword(email: string): Promise<void> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'kingdombiblequiz://reset-password'
      });

      if (error) throw error;

      track('password_reset_requested', { email });
    } catch (error: any) {
      console.error('Password reset error:', error);
      throw new Error(error.message || 'Failed to send reset email');
    }
  }

  /**
   * Update password
   */
  async updatePassword(newPassword: string): Promise<void> {
    try {
      this.assertPasswordPolicy(newPassword);

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      track('password_updated');
    } catch (error: any) {
      console.error('Password update error:', error);
      throw new Error(error.message || 'Failed to update password');
    }
  }

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle(): Promise<User | null> {
    try {
      console.log('🚀 [Google OAuth] Starting Google sign-in process');
      console.log('🚀 [Google OAuth] Platform:', Platform.OS);

      // For web platform, use OAuth with proper redirect handling
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
        const redirectTo = `${window.location.origin}/auth/callback`;
        console.log('🌐 [Google OAuth] Web platform - redirect URL:', redirectTo);

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo,
            queryParams: {
              access_type: 'offline',
              prompt: 'consent',
            },
          },
        });

        if (error) {
          console.error('❌ [Google OAuth] OAuth error:', error);
          throw new Error(this.getReadableAuthError(error.message));
        }

        if (data?.url) {
          console.log('🌐 [Google OAuth] Redirecting to Google:', data.url);
          window.location.href = data.url;
          return null; // Will redirect, so return null
        }
      } else {
        // For mobile platforms (iOS/Android), force custom scheme deep link so we return to the app
        const redirectTo = 'kingdombiblequiz://auth/callback';
        console.log('📱 [Google OAuth] Mobile platform - redirect URL:', redirectTo);

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo,
            skipBrowserRedirect: true, // Don't redirect, return the URL instead
            queryParams: {
              access_type: 'offline',
              prompt: 'consent',
            },
          },
        });

        if (error) {
          console.error('❌ [Google OAuth] OAuth error:', error);
          throw new Error(this.getReadableAuthError(error.message));
        }

        if (data?.url) {
          console.log('📱 [Google OAuth] Opening auth session:', data.url);
          const result = await WebBrowser.openAuthSessionAsync(
            data.url,
            redirectTo,
            { showInRecents: true }
          );
          // Only the type: on success the result's url carries the authorization code.
          console.log('📱 [Google OAuth] Auth session result:', result.type);

          if (result.type === 'success' && result.url) {
            console.log('📱 [Google OAuth] Auth successful, processing callback URL');
            const url = new URL(result.url);
            const code = url.searchParams.get('code');
            if (code) {
              console.log('📱 [Google OAuth] Exchanging authorization code for session');
              try {
                const { data: exchangeData, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
                if (exchangeError) {
                  console.error('❌ [Google OAuth] Code exchange error:', exchangeError);
                  throw new Error('Could not complete sign in. Please try again.');
                }
                if (exchangeData?.session) {
                  console.log('✅ [Google OAuth] Session established from code exchange');
                }
              } catch (e) {
                console.error('❌ [Google OAuth] Exchange threw:', e);
                throw e;
              }
            }
          } else if (result.type === 'cancel') {
            throw new Error('Sign in was canceled');
          }
        }
      }

      // Wait for session to be established
      return await this.waitForOAuthSession();
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      throw new Error(error.message || 'Failed to sign in with Google');
    }
  }

  /**
   * Wait for OAuth session to be established and create user profile
   */
  private async waitForOAuthSession(): Promise<User> {
    console.log('⏳ [OAuth] Waiting for session to be established...');

    let attempts = 0;
    const maxAttempts = 40; // Increased for iPad compatibility
    const delayMs = 500; // Check every 500ms

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, delayMs));

        // Try to refresh the session first (important for iPad)
        try {
          const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError && refreshedSession?.user) {
            console.log('✅ [OAuth] Session refreshed successfully (iPad fix)');
            const user = await this.createOrUpdateOAuthProfile(refreshedSession.user);
            this.currentUser = user;
            await this.syncRevenueCatIdentity(user.id);
            amplitude.setUserId(user.id);
            track('user_signed_in', {
              method: refreshedSession.user.app_metadata?.provider || 'oauth',
              language: user.language
            });
            return user;
          }
        } catch (refreshError) {
          console.log(`⏳ [OAuth] Refresh attempt ${attempts + 1} - no session yet`);
        }

        // Fallback to getSession
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        // Log session error if present but continue trying
        if (sessionError) {
          console.warn(`⚠️ [OAuth] Session error (attempt ${attempts + 1}):`, sessionError.message);
        }

        if (session?.user) {
          console.log('✅ [OAuth] Session established successfully after', (attempts + 1) * delayMs, 'ms');

          try {
            // Create or update user profile
            const user = await this.createOrUpdateOAuthProfile(session.user);
            this.currentUser = user;
            await this.syncRevenueCatIdentity(user.id);

            // Set analytics user ID
            amplitude.setUserId(user.id);

            // Track sign in event
            track('user_signed_in', {
              method: session.user.app_metadata?.provider || 'oauth',
              language: user.language
            });

            return user;
          } catch (profileError: any) {
            console.error('❌ [OAuth] Profile creation error:', profileError);
            // If profile creation fails but session exists, still throw meaningful error
            throw new Error('Session established but profile creation failed. Please try again.');
          }
        }

        attempts++;
        console.log(`⏳ [OAuth] Attempt ${attempts}/${maxAttempts} - no session yet`);
      } catch (error: any) {
        // If it's our own thrown error, re-throw it
        if (error.message?.includes('profile creation failed')) {
          throw error;
        }
        console.error(`❌ [OAuth] Attempt ${attempts + 1} failed:`, error.message);
        attempts++;
      }
    }

    // Final check - maybe session was established but we missed it
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        console.log('✅ [OAuth] Session found on final check - proceeding with profile creation');
        const user = await this.createOrUpdateOAuthProfile(session.user);
        this.currentUser = user;
        await this.syncRevenueCatIdentity(user.id);
        amplitude.setUserId(user.id);
        return user;
      }
    } catch (finalError) {
      console.error('❌ [OAuth] Final session check failed:', finalError);
    }

    throw new Error('OAuth session could not be established. Please try again.');
  }

  /**
   * Sign in with Apple (iOS native + Web OAuth + Android Web OAuth)
   */
  async signInWithApple(): Promise<User | null> {
    try {
      console.log('🍎 [Apple Auth] Starting Apple sign-in...');
      console.log('🍎 [Apple Auth] Platform:', Platform.OS);

      // For iOS native
      if (Platform.OS === 'ios') {
        return await this.signInWithAppleNative();
      }

      // For web platform
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
        return await this.signInWithAppleWeb();
      }

      // For Android, use WebBrowser for OAuth flow
      if (Platform.OS === 'android') {
        return await this.signInWithAppleAndroid();
      }

      throw new Error('Apple Sign In is only available on iOS, Android, and web platforms');
    } catch (error: any) {
      console.error('Apple sign-in error:', error);

      if (error.code === 'ERR_CANCELED') {
        throw new Error('Apple Sign In was canceled');
      }

      throw new Error(error.message || 'Failed to sign in with Apple');
    }
  }

  /**
   * Native iOS Apple Sign In
   */
  private async signInWithAppleNative(): Promise<User> {
    try {
      console.log('🍎 [Apple Auth] Using native iOS authentication');

      const credential = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
      });

      console.log('🍎 [Apple Auth] Credential received for user:', credential.user);

      if (!credential.identityToken) {
        throw new Error('Apple Sign In did not return an identity token');
      }

      // Exchange Apple credential for Supabase session
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: 'apple',
        token: credential.identityToken,
      });

      if (error) {
        console.error('❌ [Apple Auth] Token exchange error:', error);
        throw error;
      }

      if (!data.user) {
        throw new Error('No user returned from Apple authentication');
      }

      console.log('✅ [Apple Auth] Token exchange successful, user:', data.user.id);

      if (data.session?.user?.id === data.user.id) {
        console.log('✅ [Apple Auth] Session established immediately from token exchange');
        const user = await this.createOrUpdateOAuthProfile(data.session.user, {
          firstName: credential.fullName?.givenName,
          lastName: credential.fullName?.familyName,
        });

        this.currentUser = user;
        await this.syncRevenueCatIdentity(user.id);

        // Set analytics user ID
        amplitude.setUserId(user.id);

        // Track sign in event
        track('user_signed_in', {
          method: 'apple',
          language: user.language
        });

        console.log('✅ [Apple Auth] Profile created/updated');
        return user;
      }

      console.log('⏳ [Apple Auth] Session not immediately available, waiting for persisted session...');

      const user = await this.waitForVerifiedAppleSession(data.user.id, {
        firstName: credential.fullName?.givenName,
        lastName: credential.fullName?.familyName,
      });

      console.log('✅ [Apple Auth] Profile created/updated');
      return user;
    } catch (error: any) {
      if (error.code === 'ERR_CANCELED') {
        console.log('🍎 [Apple Auth] User canceled sign-in');
        throw new Error('Apple Sign In was canceled');
      }
      console.error('❌ [Apple Auth] Native sign-in failed:', error);
      throw error;
    }
  }

  /**
   * Android Apple Sign In (OAuth via WebBrowser)
   */
  private async signInWithAppleAndroid(): Promise<User> {
    try {
      console.log('🍎 [Apple Auth] Using Android OAuth flow');

      const redirectTo = Linking.createURL('/auth/callback');
      console.log('🍎 [Apple Auth] Android redirect URL:', redirectTo);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo,
          skipBrowserRedirect: true,
          queryParams: {
            response_mode: 'form_post',
          },
        },
      });

      if (error) {
        console.error('❌ [Apple Auth] OAuth error:', error);
        throw new Error(this.getReadableAuthError(error.message));
      }

      if (data?.url) {
        console.log('🍎 [Apple Auth] Opening auth session:', data.url);
        const result = await WebBrowser.openAuthSessionAsync(data.url, redirectTo);
        // Only the type: on success the result's url carries the session tokens or authorization code.
        console.log('🍎 [Apple Auth] Auth session result:', result.type);

        if (result.type === 'success' && result.url) {
          console.log('🍎 [Apple Auth] Auth successful, processing callback URL');
        }
      }

      // Wait for session to be established
      return await this.waitForOAuthSession();
    } catch (error: any) {
      console.error('❌ [Apple Auth] Android sign-in failed:', error);
      throw error;
    }
  }

  /**
   * Web Apple Sign In (OAuth)
   */
  private async signInWithAppleWeb(): Promise<User | null> {
    try {
      console.log('🍎 [Apple Auth] Using web OAuth flow');

      if (!window || !window.location) {
        throw new Error('Web environment not available');
      }

      const redirectTo = `${window.location.origin}/auth/callback`;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo,
          queryParams: {
            response_mode: 'form_post',
          },
        },
      });

      if (error) {
        console.error('❌ [Apple Auth] OAuth error:', error);
        throw new Error(this.getReadableAuthError(error.message));
      }

      if (data?.url) {
        console.log('🍎 [Apple Auth] Redirecting to Apple:', data.url);
        window.location.href = data.url;
        return null; // Will redirect, so return null
      }

      // Wait for session to be established
      return await this.waitForOAuthSession();
    } catch (error: any) {
      console.error('❌ [Apple Auth] Web sign-in failed:', error);
      throw error;
    }
  }

  /**
   * Create or update user profile for OAuth users
   */
  async createOrUpdateOAuthProfile(
    authUser: any,
    additionalData?: { firstName?: string | null; lastName?: string | null }
  ): Promise<User> {
    try {
      const existingProfile = await this.loadUserProfileOrNull(authUser.id);

      let derivedName = authUser.user_metadata?.full_name ||
                        authUser.user_metadata?.name ||
                        authUser.email?.split('@')[0] ||
                        'User';

      // Handle Apple Sign In name (provided only on first sign-in)
      if (additionalData?.firstName && additionalData?.lastName) {
        derivedName = `${additionalData.firstName} ${additionalData.lastName}`.trim();
      } else if (additionalData?.firstName) {
        derivedName = additionalData.firstName;
      }

      const language = existingProfile?.language || authUser.user_metadata?.language || useLanguageStore.getState().language || 'en';
      const avatarUrl =
        existingProfile?.avatar_url ||
        authUser.user_metadata?.avatar_url ||
        authUser.user_metadata?.picture;
      const referralCode = existingProfile?.referral_code || this.generateReferralCode();
      const now = new Date().toISOString();

      const userProfile: Partial<User> = {
        id: authUser.id,
        email: authUser.email || existingProfile?.email || '',
        name: existingProfile?.name || derivedName,
        language,
        avatar_url: avatarUrl,
        is_admin: existingProfile?.is_admin ?? false,
        onboarding_completed: existingProfile?.onboarding_completed ?? false,
        referral_code: referralCode,
        referred_by: existingProfile?.referred_by,
        total_referrals: existingProfile?.total_referrals ?? 0,
        successful_referrals: existingProfile?.successful_referrals ?? 0,
        created_at: existingProfile?.created_at || now,
        updated_at: now
      };

      // Use upsert to handle both new and existing users
      const { data, error } = await supabase
        .from('user_profiles')
        .upsert(userProfile, { 
          onConflict: 'id',
          ignoreDuplicates: false 
        })
        .select()
        .single();

      if (error) {
        console.error('Profile upsert error:', error);
        throw error;
      }

      console.log('✅ [OAuth] User profile created/updated successfully');
      return data as User;
    } catch (error: any) {
      console.error('OAuth profile creation error:', error);
      throw new Error(error.message || 'Failed to create user profile');
    }
  }

  async getLinkedAuthIdentities(): Promise<LinkedAuthIdentity[]> {
    try {
      const { data, error } = await supabase.auth.getUserIdentities();
      if (error) throw error;

      return (data?.identities || []).map((identity: any) => ({
        identityId: identity.identity_id || identity.id,
        provider: identity.provider,
        email: identity.identity_data?.email || identity.email,
        lastSignInAt: identity.last_sign_in_at,
        createdAt: identity.created_at,
      }));
    } catch (error: any) {
      console.error('Failed to load linked auth identities:', error);
      throw new Error(error.message || 'Failed to load linked auth identities');
    }
  }

  async hasLinkedAuthProvider(provider: 'email' | 'google' | 'apple'): Promise<boolean> {
    const identities = await this.getLinkedAuthIdentities();
    return identities.some((identity) => identity.provider === provider);
  }

  async addPasswordIdentity(password: string): Promise<void> {
    try {
      this.assertPasswordPolicy(password);

      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Failed to add password identity:', error);
      throw new Error(error.message || 'Failed to add password identity');
    }
  }

  /**
   * Delete user account
   */
  async deleteAccount(reason?: string): Promise<void> {
    try {
      if (!this.currentUser) throw new Error('No authenticated user');

      // Get fresh user data from Supabase Auth to check provider
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('No authenticated user');

      // Check if user is OAuth user (Google/Apple)
      const authProvider = authUser.app_metadata?.provider;
      const isOAuthUser = authProvider === 'google' || authProvider === 'apple';

      console.log('[Delete Account] OAuth user detected:', isOAuthUser, 'Provider:', authProvider);

      // For email/password users only, require recent re-authentication
      // OAuth users can't easily re-authenticate, so we skip this check for them
      if (!isOAuthUser && !this.hasRecentReauth()) {
        console.warn('[Delete Account] Re-authentication required for email user');
        throw new Error('Re-authentication required');
      }

      console.log('[Delete Account] Re-auth check passed, proceeding with deletion');

      const userId = this.currentUser.id;

      // Get current session for authorization
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('No active session');
      }

      // Call Edge Function to delete account (requires service role)
      const { data, error } = await supabase.functions.invoke('delete-account', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: { reason: reason || 'user_confirmed' },
      });

      if (error) {
        console.error('Edge function error:', error);
        console.error('Edge function error details:', JSON.stringify(error));
        throw error;
      }

      if (!data?.success) {
        console.error('Edge function returned error:', data?.error);
        throw new Error(data?.error || 'Failed to delete account');
      }

      // Track account deletion
      track('account_deleted', {
        user_id: userId
      });

      // Sign out and clear current user
      await supabase.auth.signOut();
      this.currentUser = null;
      amplitude.setUserId(undefined);

      console.log('✅ Account deleted successfully');
    } catch (error: any) {
      console.error('Account deletion error:', error);
      throw new Error(error.message || 'Failed to delete account');
    }
  }

  /**
   * Migrate guest user data to authenticated user profile
   * Called after successful sign up/sign in from guest mode
   */
  async migrateGuestData(guestProgress: any): Promise<void> {
    try {
      if (!this.currentUser) throw new Error('No authenticated user');

      console.log('📦 [Migration] Starting guest data migration for user:', this.currentUser.id);
      console.log('📦 [Migration] Guest progress:', guestProgress);

      // 1. Create quiz completion records for all guest quizzes
      if (guestProgress.categoryProgress) {
        const categoryEntries = Object.entries(guestProgress.categoryProgress) as [string, any][];

        for (const [categoryId, categoryData] of categoryEntries) {
          if (categoryData.quizzes > 0) {
            console.log(`📦 [Migration] Migrating category ${categoryId}: ${categoryData.quizzes} quizzes`);

            // Record quiz completions for this category
            // Note: We don't have individual quiz records, so we create aggregate stats
            const accuracy = categoryData.totalQuestions > 0
              ? Math.round((categoryData.correctAnswers / categoryData.totalQuestions) * 100)
              : 0;

            // Update user stats (this will be aggregated across all categories)
            await supabase.from('user_stats').upsert({
              user_id: this.currentUser.id,
              category_id: parseInt(categoryId),
              quizzes_completed: categoryData.quizzes,
              total_score: categoryData.score,
              correct_answers: categoryData.correctAnswers,
              total_questions: categoryData.totalQuestions,
              accuracy_percentage: accuracy,
              last_played_at: guestProgress.lastPlayedAt,
              created_at: guestProgress.firstPlayedAt,
              updated_at: new Date().toISOString()
            });
          }
        }
      }

      // 2. Update user profile with aggregate stats
      await this.updateProfile({
        // Store guest session metadata if needed
        // This could include first_played_at, total_guest_quizzes, etc.
      });

      console.log('✅ [Migration] Guest data migration completed successfully');

      // Track migration event
      track('guest_data_migrated', {
        user_id: this.currentUser.id,
        quizzes_migrated: guestProgress.quizzesCompleted,
        categories_migrated: Object.keys(guestProgress.categoryProgress || {}).length,
        total_score: guestProgress.totalScore
      });

    } catch (error: any) {
      console.error('❌ [Migration] Failed to migrate guest data:', error);
      // Don't throw - migration failure shouldn't block sign-up
      // Just log the error and continue
      track('guest_data_migration_failed', {
        user_id: this.currentUser?.id,
        error: error.message
      });
    }
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.currentUser?.is_admin || false;
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChange(callback: (user: User | null) => void) {
    return supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event, session?.user?.email_confirmed_at ? 'confirmed' : 'unconfirmed');
      
      if (event === 'SIGNED_IN' && session?.user) {
        try {
          // Check if user has confirmed email
          if (!session.user.email_confirmed_at) {
            console.log('User signed in but email not confirmed yet');
            this.currentUser = null;
            callback(null);
            return;
          }

          // Try to load existing profile
          try {
            const user = await this.loadUserProfile(session.user.id);
            this.currentUser = user;
            await this.syncRevenueCatIdentity(user.id);
            callback(user);
          } catch (error: any) {
            // If profile doesn't exist, create it (email confirmation completed)
            if (error.message?.includes('not found')) {
              console.log('Profile not found, creating after email confirmation');
              const user = await this.handleEmailConfirmation(session.user);
              await this.syncRevenueCatIdentity(user.id);
              callback(user);
            } else {
              console.error('Failed to load user on auth change:', error);
              callback(null);
            }
          }
        } catch (error) {
          console.error('Failed to handle auth state change:', error);
          callback(null);
        }
      } else if (event === 'SIGNED_OUT') {
        this.currentUser = null;
        callback(null);
      } else if (event === 'TOKEN_REFRESHED' && session?.user?.email_confirmed_at) {
        // Handle token refresh for confirmed users
        try {
          if (!this.currentUser) {
            const user = await this.loadUserProfile(session.user.id);
            this.currentUser = user;
            callback(user);
          }
        } catch (error) {
          console.error('Failed to handle token refresh:', error);
        }
      }
    });
  }

  private async waitForVerifiedAppleSession(
    expectedUserId: string,
    additionalData?: { firstName?: string | null; lastName?: string | null }
  ): Promise<User> {
    let attempts = 0;
    const maxAttempts = 20;

    while (attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      attempts += 1;

      try {
        try {
          const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError && refreshedSession?.user?.id === expectedUserId) {
            console.log(`✅ [Apple Auth] Verified refreshed session on attempt ${attempts}`);
            const user = await this.createOrUpdateOAuthProfile(refreshedSession.user, additionalData);
            this.currentUser = user;
            await this.syncRevenueCatIdentity(user.id);
            amplitude.setUserId(user.id);
            track('user_signed_in', {
              method: 'apple',
              language: user.language
            });
            return user;
          }
        } catch (refreshError) {
          console.warn(`[Apple Auth] Session refresh attempt ${attempts} failed:`, refreshError);
        }

        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id === expectedUserId) {
          console.log(`✅ [Apple Auth] Verified persisted session on attempt ${attempts}`);
          const user = await this.createOrUpdateOAuthProfile(session.user, additionalData);
          this.currentUser = user;
          await this.syncRevenueCatIdentity(user.id);
          amplitude.setUserId(user.id);
          track('user_signed_in', {
            method: 'apple',
            language: user.language
          });
          return user;
        }
      } catch (error) {
        console.warn(`[Apple Auth] Session verification attempt ${attempts} failed:`, error);
      }
    }

    throw new Error('Apple Sign In completed but the session did not persist. Please try again.');
  }

  private async syncRevenueCatIdentity(userId: string): Promise<void> {
    if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
      return;
    }

    try {
      await revenueCatService.identifyUser(userId);
    } catch (error) {
      console.warn('⚠️ [RevenueCat] Failed to sync user identity:', error);
    }
  }

  private async clearRevenueCatIdentity(): Promise<void> {
    if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
      return;
    }

    try {
      await revenueCatService.logout();
    } catch (error) {
      console.warn('⚠️ [RevenueCat] Failed to clear user identity:', error);
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
